import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

// Types
export interface Lot {
  id: string;
  name: string;
  slug: string;
  display_name: string;
  address?: string;
  description?: string;
  status: 'active' | 'inactive' | 'suspended';
  stripe_account_id?: string;
  owner_id?: string;
  created_at: string;
  updated_at: string;
}

export interface LotSettings {
  id: string;
  lot_id: string;
  hourly_rate: number;
  max_daily_rate: number;
  max_hours: number;
  subscription_amount: number;
  scheduled_subscriptions_enabled: boolean;
  max_monthly_subscriptions: number | null;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_phone_number?: string;
  notification_threshold: number;
  notification_enabled: boolean;
  public_site_url?: string;
  custom_branding?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface LotContextType {
  lot: Lot | null;
  settings: LotSettings | null;
  userRole: 'none' | 'checker' | 'admin' | 'owner';
  isLoading: boolean;
  error: string | null;
  refreshLot: () => Promise<void>;
}

// Create context
const LotContext = createContext<LotContextType | undefined>(undefined);

// Hook to use the lot context
export const useLot = (): LotContextType => {
  const context = useContext(LotContext);
  if (context === undefined) {
    throw new Error('useLot must be used within a LotProvider');
  }
  return context;
};

// Provider component
interface LotProviderProps {
  children: ReactNode;
}

export const LotProvider: React.FC<LotProviderProps> = ({ children }) => {
  const { lotSlug } = useParams<{ lotSlug: string }>();
  const navigate = useNavigate();
  
  const [lot, setLot] = useState<Lot | null>(null);
  const [settings, setSettings] = useState<LotSettings | null>(null);
  const [userRole, setUserRole] = useState<'none' | 'checker' | 'admin' | 'owner'>('none');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Add at top of LotProvider component, after state declarations
  const roleCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inside checkUserRole, immediately after the function starts
  if (roleCheckTimeoutRef.current) {
    clearTimeout(roleCheckTimeoutRef.current);
    roleCheckTimeoutRef.current = null;
  }

  // Fetch lot data
  const fetchLotData = async (slug: string) => {
    try {
      console.log('[LotContext] Starting to fetch lot data for slug:', slug);
      
      // Prevent multiple simultaneous fetches
      if (isFetching) {
        console.log('[LotContext] Already fetching, skipping duplicate call');
        return;
      }
      
      setIsFetching(true);
      setIsLoading(true);
      setError(null);

      // Fetch lot information
      console.log('[LotContext] Fetching lot data...');
      const { data: lotData, error: lotError } = await supabase
        .from('lots')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'active')
        .single();

      if (lotError) {
        console.log('[LotContext] Lot error:', lotError);
        throw new Error(`Lot not found: ${slug}`);
      }

      console.log('[LotContext] Lot data received:', lotData);
      setLot(lotData);

      // Fetch lot settings
      console.log('[LotContext] Fetching lot settings...');
      const { data: settingsData, error: settingsError } = await supabase
        .from('lot_settings')
        .select('*')
        .eq('lot_id', lotData.id)
        .single();

      if (settingsError) {
        console.log('[LotContext] Settings error:', settingsError);
        throw new Error('Lot settings not found');
      }

      console.log('[LotContext] Settings data received:', settingsData);
      setSettings(settingsData);

      // Check user role for this lot
      console.log('[LotContext] Checking user role...');
      await checkUserRole(slug, lotData);

      console.log('[LotContext] Lot data fetch completed successfully');
    } catch (err) {
      console.log('[LotContext] Error in fetchLotData:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load lot data';
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Redirect to home if lot not found
      if (errorMessage.includes('Lot not found')) {
        navigate('/', { replace: true });
      }
    } finally {
      console.log('[LotContext] Setting isLoading to false');
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  // Check user's role for the current lot
  const checkUserRole = async (slug: string, lotData?: Lot) => {
    // Add timeout to RPC call to prevent hanging
    console.log('[LotContext] Calling get_user_lot_access RPC...');
    
    // Create a promise with timeout for RPC call
    const { data: lotAccess, error } = await supabase.rpc('get_user_lot_access');

    if (error) {
      console.error('[LotContext] RPC Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      setUserRole('none');
      return;
    }

    console.log('[LotContext] Lot access data received:', lotAccess);

    if (!lotAccess) {
      console.log('[LotContext] No lot access data returned - this means user may not be logged in or have access.');
      setUserRole('none');
      return;
    }

    // Use the lot data passed in or fall back to the state
    const currentLot = lotData || lot;
    if (!currentLot) {
      console.log('[LotContext] Lot data not available yet, keeping current role');
      // Don't override an existing authenticated role when lot data isn't ready
      // This prevents overriding our fallback 'owner' role during race conditions
      return;
    }

    // Find the role for this lot  
    const userLotAccess = lotAccess?.find((access: any) => access.lot_id === currentLot.id);
    const role = userLotAccess?.role || 'none';
    
    if (role === 'none') {
      console.log('[LotContext] ❌ No access to lot:', slug);
    } else {
      console.log('[LotContext] ✅ Authenticated as:', role);
    }
    
    setUserRole(role as 'none' | 'checker' | 'admin' | 'owner');
    
    // Clear the safety timeout on success
    if (roleCheckTimeoutRef.current) {
      clearTimeout(roleCheckTimeoutRef.current);
      roleCheckTimeoutRef.current = null;
    }

  };

  // Refresh lot data
  const refreshLot = async () => {
    if (lotSlug) {
      await fetchLotData(lotSlug);
    }
  };

  // Effect to load lot data when slug changes
  useEffect(() => {
    if (lotSlug && isSessionReady) {
      fetchLotData(lotSlug);
    } else if (!lotSlug) {
      // If no lot slug, clear data
      setLot(null);
      setSettings(null);
      setUserRole('none');
      setIsLoading(false);
      setError(null);
      setIsFetching(false);
    }
  }, [lotSlug, isSessionReady]);

  // Listen for auth state changes
  useEffect(() => {
    // Set initial loading state
    setIsLoading(true);
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[LotContext] Auth event:', event);
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          setIsSessionReady(true);
        }

        // When user logs out, we need to reset state immediately
        if(event === 'SIGNED_OUT') {
            setLot(null);
            setSettings(null);
            setUserRole('none');
            setIsLoading(false);
            setError(null);
            setIsFetching(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value: LotContextType = {
    lot,
    settings,
    userRole,
    isLoading,
    error,
    refreshLot
  };

  return (
    <LotContext.Provider value={value}>
      {children}
    </LotContext.Provider>
  );
}; 